import React, { useEffect, useState } from 'react';
import axios from 'axios';

import SearchIcon from '@mui/icons-material/Search';
import { Alert, Dialog, DialogTitle, DialogContent, InputAdornment } from '@mui/material';
import { Grid, Typography, TextField, Button, Divider, IconButton, Select, MenuItem, InputLabel } from '@mui/material';

import NoCrashIcon from '@mui/icons-material/NoCrash';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { Person, ArrowBack } from '@mui/icons-material';
import AlertComponent from '../Alert';
import { Pagination } from '@mui/material';

import { useAuth } from '../../../Auth/AuthContext';
import { useTheme } from '@emotion/react';
import { getUniqueActiveCashbox, resolveAuthenticatedUser } from '../../../utils/cashbox';
import { normalizeApiMoney } from '../../../utils/money';
import { notifySuccessfulVehicleExit } from '../../../utils/exitHighlight';
import { createAvailableStaysLoader, initialAvailableStaysState } from '../../../utils/availableStaysLoader';
import AvailableStaysList from './AvailableStaysList';
import { sessionRequestConfig } from '../../../utils/csrf';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';

import { Backdrop, CircularProgress } from '@mui/material';

function calcularDiferenciaFechas(inicio, fin) {
  const fechaInicio = new Date(inicio);
  const fechaFin = new Date(fin);

  const diferencia = fechaFin - fechaInicio;

  if (isNaN(diferencia)) {
    console.error('Error en fechas:', { inicio, fin });
    return 'Error de fecha';
  }

  const segundosTotales = Math.floor(diferencia / 1000);
  const dias = Math.floor(segundosTotales / (3600 * 24));
  const horas = Math.floor((segundosTotales % (3600 * 24)) / 3600);
  const minutos = Math.floor((segundosTotales % 3600) / 60);
  const segundos = segundosTotales % 60;

  return `${dias} días, ${horas} horas, ${minutos} minutos y ${segundos} segundos`;
}

const API_URL = process.env.REACT_APP_API_URL;

async function actualizarReporteDiario(valor) {
  const hoy = new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Bogota' });//`${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, "0")}-${String(fecha.getDate()).padStart(2, "0")}`;
  try {
      const response = await axios.get(`${API_URL}/reporte_diario/`);

      const reportes = await response.data, reporteHoy = reportes.find(r => r.fecha === hoy && r.tipo === "E-I");
      if (reporteHoy) {
          const nuevoValor = parseFloat(reporteHoy.valor) + Number(valor);
          await axios.put(`${API_URL}/reporte_diario/${reporteHoy.id}/`, { valor: nuevoValor } );
      } else {
          await axios.post(`${API_URL}/reporte_diario/`, { fecha: hoy, tipo: "E-I", valor: Number(valor) });
      }
  } catch (error) {
      console.error("Error en la actualización del reporte:", error);
  }
}

const SalidaVehiculos = ({ handleChangePage, placaVehiculoCodigoDeBarras, onCreatedRecord }) => {
  const { user } = useAuth();
  const theme = useTheme()
  
  const [loading, setLoading] = useState(false);
  const submitRef = React.useRef(false);

  const [availableState, setAvailableState] = useState(initialAvailableStaysState);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStay, setSelectedStay] = useState(null);
  const [availabilityError, setAvailabilityError] = useState('');
  const [openAlert, setOpenAlert] = useState(false);

  const rowsPerPage = 5;

  const availableLoaderRef = React.useRef(null);
  if (availableLoaderRef.current === null) {
    availableLoaderRef.current = createAvailableStaysLoader({
      request: async ({ search, page, signal }) => {
        const response = await axios.get(`${API_URL}/estadias/listado/`, {
          params: {
            search,
            available_for_exit: true,
            page,
            page_size: rowsPerPage,
          },
          signal,
        });
        return response.data;
      },
      onStateChange: setAvailableState,
    });
  }

  const [currentTime, setCurrentTime] = useState(new Date());

  const [observaciones, setObservaciones] = useState('')

  const [formData, setFormData] = useState({
    pago: '',
    entrada: '',
    historial: '',
    cliente: '',
    estadiaId: null,
  });

  const [errors, setErrors] = useState({
    pago: false,
    entrada: false,
  });

  const selectStay = React.useCallback((stay) => {
    setSelectedStay(stay);
    setFormData((previous) => ({
      ...previous,
      entrada: stay.entrada,
      historial: stay.historial,
      cliente: stay.cliente,
      tarifa: stay.tarifa,
      usuario_entrada: stay.usuario_entrada,
      estadiaId: stay.id,
    }));
    setErrors((previous) => ({ ...previous, entrada: false }));
    setAvailabilityError('');
    setOpenDialogSearch(false);
  }, []);

  const clearSelectedStay = React.useCallback(() => {
    setSelectedStay(null);
    setFormData((previous) => ({
      ...previous,
      pago: '',
      entrada: '',
      historial: '',
      cliente: '',
      tarifa: '',
      usuario_entrada: null,
      estadiaId: null,
    }));
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (placaVehiculoCodigoDeBarras !== '') {
      const estadia = availableState.results.find(
        (item) => item.historial.placa === placaVehiculoCodigoDeBarras
      );
      if (estadia) {
        selectStay(estadia);
      } else {
        setSearchText(placaVehiculoCodigoDeBarras);
        setCurrentPage(1);
      }
    }
  }, [placaVehiculoCodigoDeBarras, availableState.results, selectStay]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      availableLoaderRef.current?.load({ search: searchText, page: currentPage });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [searchText, currentPage]);

  useEffect(() => () => availableLoaderRef.current?.dispose(), []);

  useEffect(() => {
    if (availableState.status === 'success' && currentPage > availableState.totalPages) {
      setCurrentPage(availableState.totalPages);
    }
  }, [availableState.status, availableState.totalPages, currentPage]);

  const handleSubmit = async () => {
    if (submitRef.current || loading) return;
    submitRef.current = true;
    setLoading(true);
    const newErrors = { ...errors };
    if (formData.pago === '') newErrors.pago = true;
    if (!formData.entrada) newErrors.entrada = true;

    if (formData.pago === null) {
      submitRef.current = false;
      setLoading(false);
      return;
    }
  
    setErrors(newErrors);
  
    if (!newErrors.pago && !newErrors.entrada) {
      try {
        const authenticatedUser = await resolveAuthenticatedUser(API_URL, user);
        const cajaActual = await getUniqueActiveCashbox(API_URL, authenticatedUser);
        const canonicalPayment = normalizeApiMoney(formData.pago, { allowNegative: false });

        const newSalidaData = {
          fecha_salida: currentTime.toLocaleDateString('sv-SE', { timeZone: 'America/Bogota' }),
          hora_salida: currentTime.toLocaleTimeString('sv-SE', { hour12: false, timeZone: 'America/Bogota' }),
          valor_pagado: canonicalPayment,
          tiempo_transcurrido: calcularDiferenciaFechas(
            new Date(formData.entrada.fecha_entrada+'T'+formData.entrada.hora_entrada).toISOString(),
            new Date().toISOString()
          ),
          observaciones: observaciones === '' ? 'Nada' : observaciones
        };

        const nuevoMovimiento = {
          tipo: "Ingreso",
          isME: 'estadia',
          monto: canonicalPayment,
          observacion: `Ingreso por salida de vehículo (Cliente: ${formData.cliente.nombre} ${formData.cliente.apellidos} Vehiculo: ${formData.historial.tipo_vehiculo} ${formData.historial.color} ${formData.historial.placa})`
        };

        const registeredExitResponse = await axios.post(
          `${API_URL}/estadias/${formData.estadiaId}/registrar-salida/`,
          {
            salida: newSalidaData,
            financial: {
              caja_id: cajaActual.id,
              movimiento: nuevoMovimiento,
            },
          },
          sessionRequestConfig()
        );
  
        await actualizarReporteDiario(canonicalPayment);
        notifySuccessfulVehicleExit(registeredExitResponse, formData.estadiaId, (...target) => {
          handleChangePage('None');
          onCreatedRecord?.(...target);
        });
        setOpenAlert(true);

      } catch (error) {
        if (error?.response?.status === 409) {
          setAvailabilityError(
            getApiErrorMessage(
              error,
              'La estadía ya no está disponible para registrar una salida.'
            )
          );
          clearSelectedStay();
          setCurrentPage(1);
          await availableLoaderRef.current?.load(
            { search: searchText, page: 1 },
            { force: true }
          );
        } else {
          setAvailabilityError(getApiErrorMessage(
            error,
            'No se pudo completar la salida. Intente nuevamente.',
            ['salida', 'historial', 'cliente_recibidor', 'entrada', 'tarifa']
          ));
        }
        console.error("Error al registrar la salida y actualizar la caja:", error);
      } finally {
        submitRef.current = false;
        setLoading(false);
      }
    } else {
      submitRef.current = false;
      setLoading(false);
    }
  };

  const calcularMinutosTranscurridos = (fechaInicio, fechaFin) => {
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);
    return Math.floor(Math.abs((fin - inicio) / 60000));
  };

  useEffect(() => {
    if (formData.entrada && formData.tarifa) {
      const interval = setInterval(() => {
        const minutosTranscurridos = calcularMinutosTranscurridos(
          new Date(formData.entrada.fecha_entrada+'T'+formData.entrada.hora_entrada).toISOString(),
          new Date().toISOString()
        );        
        setFormData(prev => ({
          ...prev,
          pago: calcularPago(formData.tarifa, minutosTranscurridos)
        }));
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [formData.entrada, formData.tarifa]);

  useEffect(() => {
    if (formData.entrada && formData.tarifa) {
      const minutosTranscurridos = calcularMinutosTranscurridos(
        new Date(formData.entrada.fecha_entrada+'T'+formData.entrada.hora_entrada).toISOString(),
        new Date().toISOString()
      );
      setFormData(prev => ({
        ...prev,
        pago: calcularPago(formData.tarifa, minutosTranscurridos)
      }));
    }
  }, [formData.entrada, formData.tarifa]);

  function calcularPago(tarifa, tiempoTranscurridoEnMinutos) {
    if (!tarifa) return;

    // Convertir los valores de la tarifa a número
    const tarifaInicial = parseFloat(tarifa.tarifa_inicial);
    const tarifaAdicional = parseFloat(tarifa.tarifa_adicional);
    const horasIniciales = parseInt(tarifa.horas_iniciales, 10);
    const descuento = parseFloat(tarifa.descuento); // Descuento como número
  
    // Convertir minutos a horas (puede ser fraccional)
    const horasTotales = tiempoTranscurridoEnMinutos / 60;
    let totalAPagar = 0;
  
    if (horasTotales <= horasIniciales) {
      // Dentro del tramo inicial: se redondea cada fracción de hora hacia arriba.
      const horasCobrar = Math.ceil(horasTotales);
      totalAPagar = horasCobrar * tarifaInicial;
    } else {
      // Se cobran primero las horas iniciales completas...
      const pagoInicial = horasIniciales * tarifaInicial;
      // ...y el tiempo adicional se cobra de forma proporcional.
      const tiempoAdicional = horasTotales - horasIniciales;
      const pagoAdicional = tiempoAdicional * tarifaAdicional;
      totalAPagar = pagoInicial + pagoAdicional;
    }
  
    // Aplicar descuento si existe
    if (descuento > 0) {
      const porcentajeDescuento = descuento / 100;
      totalAPagar = totalAPagar * (1 - porcentajeDescuento);
    }
  
    // Formatear el total con dos decimales
    return totalAPagar.toFixed(2);
  }

  const [openDialogSearch, setOpenDialogSearch] = useState(false);
  const selectOptions = selectedStay && !availableState.results.some((stay) => stay.id === selectedStay.id)
    ? [selectedStay, ...availableState.results]
    : availableState.results;
  
  return (
    <Grid container spacing={2} sx={{ paddingTop: 2 }}>

    <Dialog
      open={openDialogSearch}
      onClose={() => setOpenDialogSearch(false)}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { width: { xs: 'calc(100% - 24px)', sm: '100%' }, m: { xs: 1.5, sm: 4 } } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Buscar Estadía
        <IconButton
          onClick={() => setOpenDialogSearch(false)}
          aria-label="Cerrar buscador de estadías"
          sx={{ minWidth: 44, minHeight: 44 }}
        >
          <ArrowBack />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: { xs: '75vh', sm: '70vh' }, overflowY: 'auto' }}>
        <TextField
          fullWidth
          label="Buscar por ID, placa o cliente"
          inputProps={{ 'aria-label': 'Buscar estadía disponible' }}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1);
          }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
          }}
          sx={{ mb: 2 }}
        />
        <AvailableStaysList
          state={availableState}
          searchText={searchText}
          onSelect={selectStay}
        />
        {availableState.status === 'success' && availableState.totalPages > 1 && (
          <Pagination
            count={availableState.totalPages}
            page={currentPage}
            onChange={(event, value) => setCurrentPage(value)}
            color="primary"
            getItemAriaLabel={(type, page) => `${type} página ${page ?? ''}`.trim()}
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'center',
              '& .MuiPagination-ul': { flexWrap: 'wrap', justifyContent: 'center' },
            }}
          />
        )}
      </DialogContent>

    </Dialog>



      <Backdrop
        open={loading}
        style={{ zIndex: 9999, color: '#fff' }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Grid item xs={12} sx={{ position: 'relative' }}>
        <IconButton 
          color="primary" 
          sx={{ position: 'absolute', left: 0, top: 10, zIndex: 1 }}
          onClick={() => {handleChangePage('None')}}
        >
          <ArrowBack />
        </IconButton>
      </Grid>

      <Typography variant="h3" gutterBottom textAlign='center' width='100%' sx={{ paddingBottom: 1, fontSize: { xs: '2rem', sm: '3rem' } }}>
        SALIDA DE VEHICULOS
        <NoCrashIcon sx={{ marginLeft: 1, marginBottom: 0, fontSize: 50 }} />
      </Typography>

      <Grid item xs={12}>
        <Grid container justifyContent="space-around" alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField fullWidth disabled label="Fecha Salida" value={currentTime.toLocaleDateString('sv-SE', { hour12: false, timeZone: 'America/Bogota' })} variant='filled' color="primary" />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField fullWidth disabled label="Hora Salida" value={currentTime.toLocaleTimeString('sv-SE', { timeZone: 'America/Bogota' })} variant='filled' color="primary" />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}><Divider sx={{ marginY: 2 }} /></Grid>

      {availabilityError && (
        <Grid item xs={12}>
          <Alert severity="error" aria-live="assertive">{availabilityError}</Alert>
        </Grid>
      )}

      <Grid item xs={12} sx={{ flexGrow: 1 }}>
        <Grid container spacing={4} sx={{ height: '100%' }}>
          <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container alignItems="center" justifyContent="center" marginBottom={1}>
              <Typography variant="h5" gutterBottom>CLIENTE</Typography>
              <Person sx={{ marginRight: 1, marginBottom: 1 }} />
            </Grid>
            <InputLabel>Cantidad Pagada</InputLabel>
            <TextField 
              autoFocus 
              label="Pago" 
              fullWidth
              disabled
              variant='filled'
              type='text'
              inputMode='decimal'
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, maxLength: 10 }} 
              sx={{ textTransform: 'uppercase' }}
              value={formData.pago}
            />
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container alignItems="center" justifyContent="center" marginBottom={1}>
              <Typography variant="h5" gutterBottom>VEHICULO</Typography>
              <DirectionsCarIcon sx={{ marginRight: 1, marginBottom: 1}} />
            </Grid>
            <InputLabel>Tiempo transcurrido</InputLabel>
            <TextField fullWidth disabled label='Tiempo Completo' value={formData.entrada === '' ? 'No se ha seleccionado una Entrada' : calcularDiferenciaFechas(new Date(formData.entrada.fecha_entrada+'T'+formData.entrada.hora_entrada).toISOString(), new Date().toISOString() )} variant='filled' />
          </Grid>
        </Grid>
      </Grid>

    <Grid item xs={12}>
      <InputLabel>Selecciona una Entrada para darle una salida</InputLabel>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs>
          <Select
            fullWidth
            error={errors.entrada}
            value={formData.estadiaId ?? ''}
            onChange={(e) => {
              const estadiaSeleccionada = selectOptions.find(
                (stay) => stay.id === Number(e.target.value)
              );
              if (estadiaSeleccionada) selectStay(estadiaSeleccionada);
            }}
            displayEmpty
            inputProps={{ 'aria-label': 'Seleccionar estadía disponible para salida' }}
            MenuProps={{
              PaperProps: {
                sx: { maxWidth: 'calc(100vw - 32px)', maxHeight: '60vh' },
              },
            }}
          >
            <MenuItem value=""><em>Selecciona una opción</em></MenuItem>
            {selectOptions.map((estadia) => (
              <MenuItem
                key={estadia.id}
                value={estadia.id}
                sx={{ minHeight: 48, whiteSpace: 'normal', overflowWrap: 'anywhere' }}
              >
                {`${estadia.entrada.fecha_entrada} ${estadia.entrada.hora_entrada} - Cliente: ${estadia.cliente.nombre} ${estadia.cliente.apellidos} - Vehículo: ${estadia.historial.placa} ${estadia.historial.marca} ${estadia.historial.modelo} - Tarifa: ${estadia.tarifa.nombre} - Estadía ID: ${estadia.id}`}
              </MenuItem>
            ))}
          </Select>
          {errors.entrada && <Typography color="error" variant="body2">Este campo es obligatorio</Typography>}
        </Grid>

        <Grid item>
          <IconButton 
            onClick={() => setOpenDialogSearch(true)} 
            color="primary" 
            aria-label="Abrir buscador de estadías disponibles"
            sx={{ minWidth: 44, minHeight: 44, '&:focus-visible': { outline: '3px solid currentColor' } }}
          >
            <SearchIcon />
          </IconButton>
        </Grid>
      </Grid>
    </Grid>

      <Grid item xs={12}>
        <InputLabel>Observaciones</InputLabel>
        <TextField
          autoFocus
          fullWidth
          value={observaciones}
          inputProps={{ maxLength: 200 }}
          sx={{ textTransform: 'uppercase' }}
          onChange={(e) => {setObservaciones(e.target.value)}}
        />
      </Grid>

      
      <Grid item xs={12}>
        <Typography variant="body1" color='grey' fontWeight='bold'>Se agregara automaticamente un movimiento con el dinero y la información de la salida de esta Estadia, así que NO tendras que hacerlo tú mismo</Typography>
      </Grid>

      <Grid item xs={12} sx={{ textAlign: 'center', marginTop: 3 }}>
        <Button variant={theme.palette.mode === 'dark' ? "outlined" : "contained"} color="primary" size="large" onClick={handleSubmit} disabled={loading}>
          Enviar SALIDA
        </Button>
      </Grid>

      <Grid item xs={12}><Divider sx={{ marginY: 1 }} /></Grid>
      <AlertComponent open={openAlert} setOpen={setOpenAlert} text={'Salida registrada'} severity='success' snack={true}/>
    </Grid>
  );
};

export default SalidaVehiculos;
